import type { SystemPoint } from '@/types';

/** Plan output names before calculating, so no partial or colliding export is produced. */
export const planTransform = (
  points: SystemPoint[],
  range: { from: string; to: string },
  offset: number
): { points: { source: SystemPoint; name: string }[]; error?: string } => {
  const integer = (value: string) =>
    /^\d+$/.test(value) && Number.isSafeInteger(Number(value));
  if (!Number.isSafeInteger(offset))
    return { points: [], error: 'Offset must be a whole, safe integer.' };
  if (
    (range.from && !integer(range.from)) ||
    (range.to && !integer(range.to))
  ) {
    return {
      points: [],
      error: 'Range limits must be whole numeric point identifiers.',
    };
  }
  const from = range.from ? Number(range.from) : 0;
  const to = range.to ? Number(range.to) : Infinity;
  if (from > to)
    return { points: [], error: 'From point must not exceed To point.' };
  const selected = points.filter(
    (p) =>
      (!range.from && !range.to) ||
      (integer(p.point) && Number(p.point) >= from && Number(p.point) <= to)
  );
  const names = new Set<string>();
  const output: { source: SystemPoint; name: string }[] = [];
  for (const source of selected) {
    if (
      offset !== 0 &&
      (!integer(source.point) ||
        !Number.isSafeInteger(Number(source.point) + offset) ||
        Number(source.point) + offset <= 0)
    ) {
      return {
        points: [],
        error: `Cannot offset point “${source.point}”. Use numeric identifiers with positive output numbers, or set offset to zero to preserve names.`,
      };
    }
    const name =
      offset === 0 ? source.point : String(Number(source.point) + offset);
    if (names.has(name))
      return {
        points: [],
        error: `Output name “${name}” is duplicated. Adjust the selection or renumbering.`,
      };
    names.add(name);
    output.push({ source, name });
  }
  return output.length
    ? { points: output }
    : { points: [], error: 'No Record points match this selection.' };
};
